<template>
  <div class="dashboard">
    <div>
      <HeaderTitle
        :description="'Players with the most gaming activity'"
        :title="'Most active players'"
        :icon="'leaderboard'"
        :theme="'purple'"
      >
      </HeaderTitle>

      <div class="controls">
        <!-- Enhanced Period Selector -->
        <div class="control-panel period-selector">
          <div
            class="d-flex justify-content-start align-items-center w-100 gap-4 period-sort-wrapper"
          >
            <div class="period-grid">
              <button
                v-for="period in periods"
                :key="period.value"
                @click="searchByPeriod(period.value)"
                :class="[
                  'period-button',
                  selectedPeriod === period.value ? 'selected' : '',
                ]"
              >
                <!-- Background gradient -->
                <div :class="['period-bg', period.cssClass]" />
                <!-- Selected state overlay -->
                <div
                  v-if="selectedPeriod === period.value"
                  :class="['period-bg-selected', period.cssClass]"
                />
                <!-- Content -->
                <div class="period-content">
                  <div
                    :class="[
                      'period-label',
                      selectedPeriod === period.value
                        ? 'selected'
                        : 'unselected',
                    ]"
                  >
                    {{ period.label }}
                  </div>
                </div>
                <!-- Hover glow effect -->
                <div :class="['period-glow', period.cssClass]" />
              </button>
            </div>
            <div
              class="d-flex align-items-end justify-content-start w-100 gap-2 flex-wrap sort-controls"
            >
              <div class="control-panel-sort w-100">
                <div class="sort-buttons">
                  <button
                    @click="searchBySort('totalBets')"
                    :class="[
                      'sort-button',
                      sortBy === 'totalBets' ? 'active' : 'inactive',
                    ]"
                  >
                    <Users class="icon-class" />
                    Count
                  </button>
                  <button
                    @click="searchBySort('totalBetAmount')"
                    :class="[
                      'sort-button',
                      sortBy === 'totalBetAmount' ? 'active' : 'inactive',
                    ]"
                  >
                    <DollarSign class="icon-class" />
                    Amount
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Leaderboard Container -->
      <div class="leaderboard-container">
        <div v-if="!loading" class="players-header">
          <h2 class="players-title">
            <Trophy class="w-6 h-6 icon-yellow" />
            Leaderboard
            <span class="players-subtitle">
              ({{ getSelectedPeriod()?.fullLabel }} )
            </span>
          </h2>
        </div>

        <div v-if="loading" class="loading">
          <Loader />
        </div>

        <div
          v-else-if="!loading && sortedPlayers?.length > 0"
          class="leaderboard-content"
        >
          <div v-if="sortedPlayers.length >= 3" class="podium-container">
            <div class="podium">
              <div class="podium-position second-place">
                <div
                  class="podium-player"
                  :title="`Open ${sortedPlayers[1].username} details`"
                  @click="openPlayer(sortedPlayers[1].username)"
                >
                  <div class="podium-rank silver">
                    <span class="rank-medal">🥈</span>
                    <span class="rank-number">#2</span>
                  </div>
                  <div class="podium-avatar-container">
                    <img
                      :src="sortedPlayers[1].avatar"
                      :alt="sortedPlayers[1].username"
                      class="podium-avatar"
                    />
                    <div class="podium-avatar-border silver"></div>
                  </div>
                  <div class="podium-info">
                    <h3 class="podium-name">
                      {{ sortedPlayers[1].username }}
                    </h3>
                    <div class="podium-stat">
                      <span
                        class="podium-value"
                        :class="
                          sortBy === 'totalBets' ? 'bets-value' : 'win-value'
                        "
                      >
                        {{
                          sortBy === "totalBets"
                            ? sortedPlayers[1].totalBets
                            : formatCurrency(sortedPlayers[1].totalBetAmount)
                        }}
                      </span>
                      <span class="podium-label">
                        {{ sortBy === "totalBets" ? "Bets" : "Amount" }}
                      </span>
                    </div>
                  </div>
                </div>
                <div class="podium-base silver-base">
                  <div class="podium-height second"></div>
                </div>
              </div>

              <div class="podium-position first-place">
                <div
                  class="podium-player"
                  :title="`Open ${sortedPlayers[0].username} details`"
                  @click="openPlayer(sortedPlayers[0].username)"
                >
                  <div class="podium-rank gold">
                    <span class="rank-medal">🥇</span>
                    <span class="rank-number">#1</span>
                  </div>
                  <div class="podium-avatar-container">
                    <img
                      :src="sortedPlayers[0].avatar"
                      :alt="sortedPlayers[0].username"
                      class="podium-avatar"
                    />
                    <div class="podium-avatar-border gold"></div>
                  </div>
                  <div class="podium-info">
                    <h3 class="podium-name">
                      {{ sortedPlayers[0].username }}
                    </h3>
                    <div class="podium-stat">
                      <span
                        class="podium-value"
                        :class="
                          sortBy === 'totalBets' ? 'bets-value' : 'win-value'
                        "
                      >
                        {{
                          sortBy === "totalBets"
                            ? sortedPlayers[0].totalBets
                            : formatCurrency(sortedPlayers[0].totalBetAmount)
                        }}
                      </span>
                      <span class="podium-label">
                        {{ sortBy === "totalBets" ? "Bets" : "Amount" }}
                      </span>
                    </div>
                  </div>
                </div>
                <div class="podium-base gold-base">
                  <div class="podium-height first"></div>
                </div>
              </div>

              <div class="podium-position third-place">
                <div
                  class="podium-player"
                  :title="`Open ${sortedPlayers[2].username} details`"
                  @click="openPlayer(sortedPlayers[2].username)"
                >
                  <div class="podium-rank bronze">
                    <span class="rank-medal">🥉</span>
                    <span class="rank-number">#3</span>
                  </div>
                  <div class="podium-avatar-container">
                    <img
                      :src="sortedPlayers[2].avatar"
                      :alt="sortedPlayers[2].username"
                      class="podium-avatar"
                    />
                    <div class="podium-avatar-border bronze"></div>
                  </div>
                  <div class="podium-info">
                    <h3 class="podium-name">
                      {{ sortedPlayers[2].username }}
                    </h3>
                    <div class="podium-stat">
                      <span
                        class="podium-value"
                        :class="
                          sortBy === 'totalBets' ? 'bets-value' : 'win-value'
                        "
                      >
                        {{
                          sortBy === "totalBets"
                            ? sortedPlayers[2].totalBets
                            : formatCurrency(sortedPlayers[2].totalBetAmount)
                        }}
                      </span>
                      <span class="podium-label">
                        {{ sortBy === "totalBets" ? "Bets" : "Amount" }}
                      </span>
                    </div>
                  </div>
                </div>
                <div class="podium-base bronze-base">
                  <div class="podium-height third"></div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="sortedPlayers.length > 3" class="remaining-players">
            <h3 class="remaining-players-title my-2">
              <Users class="w-5 h-5" />
              Other Players
            </h3>
            <div class="players-list">
              <div
                v-for="(player, index) in sortedPlayers.slice(3)"
                :key="player.id"
                class="player-item"
                @click="openPlayer(player.username)"
                :title="`Open ${player.username} details`"
              >
                <div class="player-content">
                  <div class="player-rank default">#{{ index + 4 }}</div>

                  <div class="player-avatar-container">
                    <img
                      :src="player.avatar"
                      :alt="player.username"
                      class="player-avatar avatar-after-third ms-2"
                    />
                  </div>

                  <div class="d-flex flex-column ps-3">
                    <div class="player-info">
                      <h3 class="player-name text-end">
                        {{ player.username }}
                      </h3>
                    </div>

                    <div class="player-primary-stat">
                      <div
                        class="primary-stat-value"
                        :class="
                          sortBy === 'totalBets' ? 'bets-value' : 'win-value'
                        "
                      >
                        {{
                          sortBy === "totalBets"
                            ? player.totalBets
                            : formatCurrency(player.totalBetAmount)
                        }}
                      </div>
                      <div class="primary-stat-label">
                        {{ sortBy === "totalBets" ? "Bets" : "Amount" }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="no-results-found" v-else>
          <div class="no-results">No results found.</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  Trophy,
  TrendingUp,
  DollarSign,
  Users,
  Calendar,
  Clock,
} from "lucide-vue-next";
import HeaderTitle from "../Utility/HeaderTitle.vue";
import { getFetch } from "@/api/axiosClient";
import Loader from "../Loader.vue";

export default {
  name: "TopPlayer",
  components: {
    Trophy,
    TrendingUp,
    DollarSign,
    Users,
    Calendar,
    Clock,
    HeaderTitle,
    Loader,
  },
  data() {
    return {
      sortBy: "totalBets",
      selectedPeriod: 1,
      loading: false,
      periods: [
        {
          value: 1,
          label: "1D",
          fullLabel: "1 Day",
          cssClass: "gradient-red-pink",
        },
        {
          value: 7,
          label: "7D",
          fullLabel: "7 Days",
          cssClass: "gradient-orange-red",
        },
        {
          value: 15,
          label: "15D",
          fullLabel: "15 Days",
          cssClass: "gradient-amber-orange",
        },
        {
          value: 30,
          label: "30D",
          fullLabel: "30 Days",
          cssClass: "gradient-amber-orange",
        },
        {
          value: 60,
          label: "60D",
          fullLabel: "60 Days",
          cssClass: "gradient-amber-orange",
        },
        {
          value: 180,
          label: "180D",
          fullLabel: "180 Days",
          cssClass: "gradient-amber-orange",
        },
        {
          value: 360,
          label: "360D",
          fullLabel: "360 Days",
          cssClass: "gradient-amber-orange",
        },
      ],
      players: [],
    };
  },
  computed: {
    sortedPlayers() {
      return [...this.players].sort((a, b) => {
        return this.sortBy === "totalBets"
          ? b.totalBets - a.totalBets
          : b.totalBetAmount - a.totalBetAmount;
      });
    },
    totalBets() {
      return this.players.reduce((acc, p) => acc + p.totalBets, 0);
    },
    totalAmount() {
      return this.players.reduce((acc, p) => acc + p.totalBetAmount, 0);
    },
    averageWinRate() {
      if (!this.players.length) return 0;
      const total = this.players.reduce((acc, p) => acc + p.winRate, 0);
      return (total / this.players.length).toFixed(1);
    },
  },
  methods: {
    async openPlayer(user_id) {
      let skin = user_id.split("_")[0];
      skin = Number(skin);
      if (!skin) return alert("Player Demo");
      try {
        const data = await getFetch(
          `players/detail/${user_id}?skin_id=${skin}`
        );
        if (data) {
          setTimeout(() => {
            window.open(`/players/${data._id}`, "_blank");
          });
        } else alert("Player detail are not present");
      } catch (error) {
        alert("Player detail are not present");
      }
    },
    getSelectedPeriod() {
      return this.periods.find((p) => p.value === this.selectedPeriod);
    },
    getRankIcon(rank) {
      if (rank === 1) return "🥇";
      if (rank === 2) return "🥈";
      if (rank === 3) return "🥉";
      return `#${rank}`;
    },
    getRankColor(rank) {
      if (rank === 1) return "gold";
      if (rank === 2) return "silver";
      if (rank === 3) return "bronze";
      return "default";
    },
    formatCurrency(num) {
      return `€  ${parseFloat(num).toFixed(2)}`;
    },
    searchByPeriod(period) {
      if (period !== this.selectedPeriod) {
        this.selectedPeriod = period;
        this.fetchData();
      }
    },
    searchBySort(sort) {
      if (sort !== this.sortBy) {
        this.sortBy = sort;
        this.fetchData();
      }
    },
    async fetchData() {
      this.loading = true;
      try {
        const data = await getFetch(
          `dashboard/bestPlayers?period=${parseInt(this.selectedPeriod)}&sort=${
            this.sortBy
          }`
        );

        let db = this.$store?.getters?.selectedDatabase || "theearcasino";

        this.players = data.data.map((item, index) => {
          return {
            id: index + 1,
            username: item.user_id,
            totalBets: this.sortBy === "totalBets" ? item.value : 0,
            totalBetAmount: this.sortBy === "totalBetAmount" ? item.value : 0,
            avatar: `https://storage.googleapis.com/camasy-images/avataraugustus/${db}.png`,
          };
        });
      } catch (error) {
        console.error("Error fetching players:", error);
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.fetchData();
  },
};
</script>

<style lang="scss" scoped>
.controls {
  margin: 1.25rem 0rem;
}

.control-panel {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.period-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
}

.control-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
}

.period-info {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #9ca3af;
  font-size: 14px;

  @media (max-width: 767px) {
    display: none;
  }
}

.period-info-selected {
  color: #a855f7;
  font-weight: 500;
}

.period-grid {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  flex: 1;
}

.sort-controls {
  flex: 1;
}

.period-button {
  position: relative;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 0.35rem;
  cursor: pointer;
  transition: all 0.3s ease;
  overflow: hidden;
  flex: 1 1 auto;
}

.period-button:hover {
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

.period-button.selected {
  border-color: #e7f755;
}

.period-content {
  position: relative;
  z-index: 2;
}

.period-label {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 4px;
}

.period-label.selected {
  color: #ffffff;
}

.period-label.unselected {
  color: #f6f6f6;
}

.period-sublabel {
  font-size: 12px;
  font-weight: 500;

  @media (max-width: 767px) {
    display: none;
  }
}

.period-sublabel.selected {
  color: #d1d5db;
}

.period-sublabel.unselected {
  color: #6b7280;
}

.sort-buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;

  @supports (-webkit-touch-callout: none) {
    zoom: 80%;
  }

  @media (max-width: 650px) {
    zoom: 80%;
  }
}

.sort-button {
  display: flex;
  align-items: center;
  padding: 0.75rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: transparent;
  color: #9ca3af;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:first-child {
    border-radius: 0.5rem 0rem 0rem 0.5rem;
  }

  &:last-child {
    border-radius: 0rem 0.5rem 0.5rem 0rem;
  }
}

.sort-button:hover {
  border-color: rgba(255, 255, 255, 0.3);
  color: #ffffff;
}

.sort-button.active {
  background: linear-gradient(45deg, #2196f3, #1976d2);
  border-color: transparent;
  box-shadow: 0 4px 15px rgba(33, 150, 243, 0.3);
  color: #ffffff;
}

/* Leaderboard Container */
.leaderboard-container {
  margin-bottom: 32px;
}

.players-header {
  margin-bottom: 24px;
}

.players-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;

  @media (max-width: 767px) {
    font-size: 17px;
  }
}

.players-subtitle {
  font-size: 16px;
  font-weight: 400;
  color: #9ca3af;
  margin-left: 8px;

  @media (max-width: 767px) {
    font-size: 13px;
  }
}

.podium {
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 1rem;
  //   padding: 0px 0.55rem;
}

.podium-position {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  flex: 1;
}

.podium-name {
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 850px) {
    max-width: 90%;
  }
}

.podium-player {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 16px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  border: 2px solid transparent;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  margin-bottom: 16px;
  min-width: 200px;
  overflow: hidden;

  &:hover {
    cursor: pointer;

    @media (min-width: 991px) {
      transform: translateY(-15px);
    }

    .podium-name,
    .player-name {
      transition: 0.3s all ease;
      cursor: pointer;
      text-decoration: underline;
    }
  }
}

.first-place .podium-player {
  border-color: #fbbf24;
  background: rgba(251, 191, 36, 0.1);

  &:hover {
    @media (min-width: 991px) {
      box-shadow: 0 8px 32px rgba(251, 191, 36, 0.3);
    }
  }
  //
}

.second-place .podium-player {
  border-color: #9ca3af;
  background: rgba(156, 163, 175, 0.1);
  &:hover {
    @media (min-width: 991px) {
      box-shadow: 0 8px 32px rgba(156, 163, 175, 0.2);
    }
  }
  //   box-shadow: 0 8px 32px rgba(156, 163, 175, 0.2);
}

.third-place .podium-player {
  border-color: #cd7c2f;
  background: rgba(205, 124, 47, 0.1);

  &:hover {
    @media (min-width: 991px) {
      box-shadow: 0 8px 32px rgba(205, 124, 47, 0.2);
    }
  }
  //   box-shadow: 0 8px 32px rgba(205, 124, 47, 0.2);
}

.podium-value {
  color: white;
}

.podium-rank {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 16px;
  position: relative;

  // @media (max-width: 767px) {
  //   display: none;
  // }
}

.rank-medal {
  font-size: 32px;
  margin-bottom: 8px;
  display: none;
  @media (min-width: 991px) {
    display: none;
  }
}

.rank-number {
  font-size: 14px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
}

.gold .rank-number {
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  color: #ffffff;
}

.silver .rank-number {
  background: linear-gradient(135deg, #9ca3af, #6b7280);
  color: #ffffff;
}

.bronze .rank-number {
  background: linear-gradient(135deg, #cd7c2f, #92400e);
  color: #ffffff;
}

.crown {
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 24px;
  animation: float 2s ease-in-out infinite;
}

@keyframes float {
  0%,
  100% {
    transform: translateX(-50%) translateY(0px);
  }

  50% {
    transform: translateX(-50%) translateY(-5px);
  }
}

.podium-avatar-container {
  position: relative;
  margin-bottom: 16px;
}

.podium-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  position: relative;
  z-index: 2;
}

.podium-avatar-border {
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  border-radius: 50%;
  z-index: 1;
}

.podium-avatar-border.gold {
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
}

.podium-avatar-border.silver {
  background: linear-gradient(135deg, #9ca3af, #6b7280);
}

.podium-avatar-border.bronze {
  background: linear-gradient(135deg, #cd7c2f, #92400e);
}

.podium-name {
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 12px 0;
  text-align: center;
}

.podium-stat {
  text-align: center;
}

.podium-value {
  display: block;
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 4px;
}

.first-place .podium-name {
  color: #fbbf24;
}

.second-place .podium-name {
  color: #9ca3af;
}

.third-place .podium-name {
  color: #cd7c2f;
}

.podium-label {
  font-size: 12px;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.podium-base {
  width: 100%;
  border-radius: 8px 8px 0 0;
  position: relative;
}

.gold-base {
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
}

.silver-base {
  background: linear-gradient(135deg, #9ca3af, #6b7280);
}

.bronze-base {
  background: linear-gradient(135deg, #cd7c2f, #92400e);
}

.podium-height.first {
  height: 80px;
}

.podium-height.second {
  height: 60px;
}

.podium-height.third {
  height: 40px;
}

.remaining-players-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 20px;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 24px;
}

.players-list {
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: flex-start;
  justify-content: flex-start;
  flex-wrap: wrap;
  zoom: 90%;

  //   @media (max-width: 650px) {
  //     zoom: 90%;
  //   }
}

.player-item {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  flex: 1;
  min-width: 48%;
  cursor: pointer;

  @media (max-width: 767px) {
    min-width: 100%;
  }
}

.player-item:hover {
  border-color: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.player-content {
  display: grid;
  grid-template-columns: auto auto 1fr auto;
  gap: 16px;
  align-items: center;
  padding: 0.55rem 1.25rem;
}

.player-rank {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  height: 32px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
}

.player-rank.default {
  background: rgba(107, 114, 128, 0.2);
  color: #9ca3af;
}

.player-avatar-container {
  position: relative;
}

.player-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.1);
}

.player-info {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.player-name {
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.player-primary-stat {
  text-align: right;
  display: flex;
  align-items: end;
  justify-content: flex-end;
  gap: 0.45rem;
}

.primary-stat-value {
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 0px;
}

.primary-stat-label {
  font-size: 12px;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0px;
}

/* Stats Summary */
.stats-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 32px;
}

.stat-card {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.stat-card:hover {
  border-color: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

.stat-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.stat-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  flex-shrink: 0;
}

.stat-icon-purple {
  background: rgba(139, 92, 246, 0.2);
}

.stat-icon-green {
  background: rgba(34, 197, 94, 0.2);
}

.stat-icon-blue {
  background: rgba(59, 130, 246, 0.2);
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 4px 0;
}

.stat-label {
  font-size: 14px;
  color: #9ca3af;
  margin: 0;
}

.podium-info {
  width: 100%;
}

/* Loading */
.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  gap: 16px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-left: 4px solid #a855f7;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.loading-text {
  color: #9ca3af;
  font-size: 16px;
  margin: 0;
}

.leaderboard-content {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  flex-wrap: wrap;
  gap: 1.5rem;

  @media (max-width: 1400px) {
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
  }

  .podium-container {
    zoom: 80%;
    flex: 1;
  }

  .remaining-players {
    flex: 1;
  }
}

@media (min-width: 1200px) {
  .podium-player {
    max-width: 200px;
  }
}

/* Responsive Design */
@media (max-width: 1199px) {
  .podium {
    flex-direction: row;
    gap: 16px;
    align-items: center;
    padding-bottom: 0.55rem;
    flex-wrap: wrap;
    justify-content: flex-start;
  }

  .podium-position {
    width: 100%;
  }

  .podium-rank {
    order: 3;
  }

  .first-place {
    order: 1;
    min-width: 100%;
  }

  .second-place {
    order: 2;
  }

  .third-place {
    order: 3;
  }

  .second {
    max-height: 30px;
  }

  .first {
    max-height: 45px;
  }

  .third {
    max-height: 20px;
  }

  .podium-stat {
    text-align: left;
  }

  .podium-avatar {
    width: 45px;
    height: 45px;
    border-radius: 50%;
    -o-object-fit: cover;
    object-fit: cover;
    position: relative;
    z-index: 2;
  }

  .podium-player {
    min-width: auto;
    width: 100%;
    padding: 0.65rem 1rem;
  }

  .podium-info {
    width: 100%;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .podium-name {
      text-align: left;
    }
  }

  .player-content {
    grid-template-columns: auto auto 1fr;
    gap: 0px;
  }

  .player-primary-stat {
    grid-column: 1 / -1;
    text-align: left;
    margin-top: 10px;
    padding-top: 5px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    justify-content: space-between;
    flex-direction: row-reverse;
  }

  .period-grid {
    grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
    gap: 8px;
  }

  .period-button {
    padding: 0.35rem;
  }

  .period-label {
    font-size: 16px;
  }

  .period-sublabel {
    font-size: 10px;
  }

  .podium-container {
    flex: none;
    width: 100%;
    min-width: 100%;
  }

  .podium-player {
    flex-direction: row;
    gap: 1rem;
    margin-bottom: 0px;
  }

  .podium-base {
    display: none;
  }
}

/* Icon Colors */
.icon-blue {
  color: #3b82f6;
}

.icon-purple {
  color: #a855f7;
}

.icon-green {
  color: #22c55e;
}

.icon-yellow {
  color: #eab308;
}

.icon-gray {
  color: #9ca3af;
}

/* Utility Classes */
.d-flex {
  display: flex;
}

.align-items-end {
  align-items: flex-end;
}

.justify-content-start {
  justify-content: flex-start;
}

.w-100 {
  width: 100%;
}

.gap-2 {
  gap: 8px;
}

.flex-wrap {
  flex-wrap: wrap;
}

.mb-2 {
  margin-bottom: 8px;
}

.px-2 {
  padding-left: 8px;
  padding-right: 8px;
}

.avatar-after-third {
  @media (max-width: 991px) {
    display: none;
  }
}

.period-sort-wrapper {
  flex-wrap: wrap;

  @media (max-width: 1199px) {
    .period-grid {
      flex: none;
      width: 100%;

      .period-button {
        @media (max-width: 767px) {
          padding: 0.55rem;
        }
      }
    }

    .sort-controls {
      flex: none;
    }
  }
}

.icon-class {
  font-size: 15px;
  width: 18px;
  height: 18px;
  margin-right: 5px;
}

.no-results-found {
  display: flex;
  justify-content: center;
  align-items: start;
  padding-top: 1rem;

  .no-results {
    color: white;
    font-weight: 500;
  }
}

.bets-value {
  color: #66d9db;
}
.win-value {
  color: #5dea5d;
}
</style>
